import { PickOrCreate } from '../primitives/PickOrCreate';
import { PropertyPill } from '../primitives/PropertyPill';
import {
  formatPropertyPillLabel,
  isPropertyPillMuted
} from '../primitives/propertyBarDisplay';
import { useDocumentProjects } from '../../lib/projects/useDocumentProjects';
import { useOrgProjects } from '../../lib/projects/useOrgProjects';
import { useProjectMembership } from '../../lib/projects/useProjectMembership';

const NO_PROJECT_VALUE = '__no_project__';

export interface DocumentProjectPillProps {
  documentId: string;
  orgId: string;
  editable: boolean;
  testIdPrefix: string;
  onError: (message: string) => void;
}

/**
 * "Project" pill for the document editor's canvas property bar — the single-value
 * mirror of the work-item Project pill, but backed by the `project_documents`
 * many-to-many (a document may live in several projects; the inspector's
 * `DocumentProjectsSection` manages the full set). The bar reads as one property:
 * it shows the linked project (or "name +N" when the doc is in several) and
 * picking sets the project — link/unlink so the document ends up in exactly the
 * chosen project, or none when "— No project —" is picked.
 */
export function DocumentProjectPill({
  documentId,
  orgId,
  editable,
  testIdPrefix,
  onError
}: DocumentProjectPillProps) {
  const { load, reload } = useDocumentProjects(documentId);
  const { projects: orgProjects } = useOrgProjects(orgId);
  const { linkDocument, unlinkDocument } = useProjectMembership();

  const linked = load.kind === 'ready' ? load.projects : [];
  const display =
    linked.length === 0
      ? null
      : linked.length === 1
        ? linked[0].name
        : `${linked[0].name} +${linked.length - 1}`;

  // While the linked set is still loading, or the org has no projects to pick
  // from, render a read-only pill — the same fallback the work-item bar uses.
  if (load.kind !== 'ready' || orgProjects.length === 0) {
    return (
      <PropertyPill
        label="Project"
        displayValue={display}
        editable={false}
        testId={`${testIdPrefix}-pill-project`}
      >
        {() => null}
      </PropertyPill>
    );
  }

  const pickOptions = [
    { id: NO_PROJECT_VALUE, label: '— No project —' },
    ...orgProjects.map((p) => ({ id: p.id, label: p.name }))
  ];
  const selectValue =
    linked.length === 1 ? linked[0].id : linked.length === 0 ? NO_PROJECT_VALUE : null;

  const setProject = async (pickedId: string) => {
    const target = pickedId === NO_PROJECT_VALUE ? null : pickedId;
    // Single-value bar semantics over the M2M: leave the doc linked to exactly
    // `target` (or nothing). Unlink anything else first, then link the target.
    for (const p of linked) {
      if (p.id === target) continue;
      const ok = await unlinkDocument(p.id, documentId);
      if (!ok) {
        onError('Could not update the document project.');
        reload();
        return;
      }
    }
    if (target && !linked.some((p) => p.id === target)) {
      const ok = await linkDocument(orgId, target, documentId);
      if (!ok) {
        onError('Could not update the document project.');
        reload();
        return;
      }
    }
    reload();
  };

  return (
    <PickOrCreate
      id="document-canvas-bar-project"
      label="Project"
      options={pickOptions}
      value={selectValue ? { option_id: selectValue } : null}
      disabled={!editable}
      triggerVariant="pill"
      pillLabel={formatPropertyPillLabel('Project', display)}
      pillMuted={isPropertyPillMuted(display)}
      data-testid={`${testIdPrefix}-pill-project`}
      onPick={(picked) => {
        if (picked) void setProject(picked.id);
      }}
      onCreate={async () => null}
    />
  );
}
